<template>
	<table class="table table-bordered">
		<thead>
			<tr class="table-light text-center">
				<th scope="col" :style="{ width: '40%' }">
					<div
						class="
							d-flex
							align-items-center
							justify-content-center
							m-auto
							gap-3
						"
					>
						<div class="btn-group btn-group-sm">
							<button
								class="btn btn-outline-secondary"
								@click="startAll()"
								title="Start all"
							>
								<i class="bi bi-play-fill" />
							</button>
							<button
								class="btn btn-outline-secondary"
								@click="pauseAll()"
								title="Pause all"
							>
								<i class="bi bi-pause-fill" />
							</button>

							<input
								type="checkbox"
								class="btn-check"
								:checked="randomize"
								@click="toggleRandomize()"
								id="randomize-checkbox"
							/>
							<label
								class="btn btn-outline-secondary bi bi-shuffle"
								for="randomize-checkbox"
								title="Randomize sitemaps"
							/>
						</div>

						<!-- request delay controls -->
						<div class="d-flex gap-1 align-items-center">
							<div>
								Delay:
								<span class="font-monospace">{{
									Math.floor(delayMS / 1000)
								}}</span
								>s
							</div>
							<div class="btn-group btn-group-sm">
								<button
									class="btn btn-outline-secondary"
									@click="setDelay(delayMS + 1000)"
									title="Increase delay"
								>
									<i class="bi bi-plus-lg" />
								</button>
								<button
									class="btn btn-outline-secondary"
									@click="setDelay(delayMS - 1000)"
									title="Decrease delay"
								>
									<i class="bi bi-dash-lg" />
								</button>
							</div>
						</div>

						<!-- max parallel requests controls -->
						<div class="d-flex gap-1 align-items-center">
							<div>
								Threads:
								<span class="font-monospace">{{
									maxRequests
								}}</span>
							</div>
							<div class="btn-group btn-group-sm">
								<button
									class="btn btn-outline-secondary"
									@click="setMaxRequests(maxRequests + 1)"
									title="Increase max requests"
								>
									<i class="bi bi-plus-lg" />
								</button>
								<button
									class="btn btn-outline-secondary"
									@click="setMaxRequests(maxRequests - 1)"
									title="Decrease max requests"
								>
									<i class="bi bi-dash-lg" />
								</button>
							</div>
						</div>
					</div>
				</th>
				<th scope="col" title="Amount of URLs in a sitemap">Total</th>
				<th scope="col" title="Amount of checked URLs">Done</th>
				<th
					v-for="status in seenStatuses"
					scope="col"
					:key="status"
					title="Server response code"
				>
					{{ status }}
				</th>
				<th scope="col" title="Sitemap checking progress">Progress</th>
			</tr>
		</thead>

		<draggable
			tag="tbody"
			:list="sitemapsWrapped"
			animation="200"
			item-key="sitemap"
			ghost-class="dragged-item"
			handle=".drag-handle"
			@end="handleDragEnd"
		>
			<template #item="{ element }">
				<SitemapRow :sitemap="element.sitemap" />
			</template>

			<template #footer>
				<tr>
					<td colspan="100">
						<form @submit.prevent="handleAddSitemap">
							<div class="input-group has-validation">
								<input
									class="form-control new-sitemap-input"
									v-model="newSitemap"
									placeholder="Sitemap"
									type="url"
									ref="newSitemapInput"
									@input="
										newSitemapInput.classList.remove(
											'is-invalid'
										)
									"
								/>
								<div class="invalid-tooltip">
									Invalid sitemap URL
								</div>
							</div>
						</form>
					</td>
				</tr>
			</template>
		</draggable>
	</table>
</template>

<script lang="ts">
import { watcherService } from "@/services/watcherService";
import { computed, defineComponent, ref } from "vue";
import Draggable from "vuedraggable";
import SitemapRow from "./SitemapRow.vue";

export default defineComponent({
	components: { SitemapRow, Draggable },
	setup() {
		const {
			sitemaps,
			sitemapStats,
			validateSitemap,
			addSitemap,
			reorderSitemaps,
			seenStatuses,
			continueWatching,
			pauseWatching,
			maxRequests,
			setMaxRequests,
			delayMS,
			setDelay,
			randomize,
			setRandomize,
		} = watcherService;

		const newSitemapInput = ref<null | HTMLInputElement>(null);
		const newSitemap = ref("");
		const handleAddSitemap = async () => {
			if (!validateSitemap(newSitemap.value)) {
				newSitemapInput.value?.classList.add("is-invalid");
				return;
			}

			newSitemapInput.value?.classList.remove("is-invalid");
			try {
				await addSitemap(newSitemap.value);
				newSitemap.value = "";
			} catch (e) {
				console.error(e);
				newSitemapInput.value?.classList.add("is-invalid");
			}
		};

		const startAll = () => {
			for (let sitemap of sitemaps.value) {
				continueWatching(sitemap);
			}
		};

		const pauseAll = () => {
			for (let sitemap of sitemaps.value) {
				pauseWatching(sitemap);
			}
		};

		const toggleRandomize = () => {
			setRandomize(!randomize.value);
		};

		// vuedraggable requires an array of objects to operate so we convert
		// sitemap urls to objects with one property
		const sitemapsWrapped = computed(() =>
			sitemaps.value.map(sitemap => ({
				sitemap,
			}))
		);
		const handleDragEnd = (event: any) => {
			const newSitemapsOrder = sitemaps.value.slice();
			const item = newSitemapsOrder.splice(event.oldIndex, 1);
			newSitemapsOrder.splice(event.newIndex, 0, ...item);
			reorderSitemaps(newSitemapsOrder);
		};

		return {
			sitemaps,
			sitemapStats,

			addSitemap,
			newSitemap,
			handleAddSitemap,
			newSitemapInput,

			seenStatuses,

			startAll,
			pauseAll,

			maxRequests,
			setMaxRequests,

			delayMS,
			setDelay,

			randomize,
			toggleRandomize,

			sitemapsWrapped,
			handleDragEnd,
		};
	},
});
</script>

<style scoped>
.dragged-item {
	opacity: 0;
	background: #eee;
}
</style>
